import { api } from './api';

export interface StripeResetIssue {
  userId: number;
  userEmail: string;
  issueType: 'missing_reset_date' | 'invalid_reset_date' | 'outdated_reset_date' | 'misaligned_anniversary_date';
  stripeData?: {
    customerId: string;
  };
  databaseData?: {
    nextUsageResetDate: string | null;
    paymentType?: number; // Add paymentType for proper subscription type detection
    subscriptionStartDate?: string | null; // Add subscription anniversary date
  };
}

export interface StripeDiagnosisRequest {
  fix?: boolean;
  limit?: number;
}

export interface SingleUserDiagnosisRequest {
  fix: boolean;
}

export interface AllUserData {
  userId: number;
  userEmail: string;
  nextUsageResetDate: string | null;
  customerId: string;
  hasIssues: boolean;
  paymentType?: number; // Add paymentType for proper subscription type detection
  subscriptionStartDate?: string | null; // Add subscription anniversary date
}

export interface StripeDiagnosisResponse {
  success: boolean;
  message: string;
  logs?: string[];
  parameters?: StripeDiagnosisRequest;
  issuesFound?: number;
  issuesFixed?: number;
  issues?: StripeResetIssue[];
  allUsers?: AllUserData[];
  updatedUser?: AllUserData; // NEW: Updated user data for efficient list updates
  summary?: {
    databaseUsersChecked: number;
    stripeUsersChecked: number;
  };
}

export interface UpdateResetDateRequest {
  userId: number;
  nextUsageResetDate: string;
}

export interface UpdateResetDateResponse {
  success: boolean;
  message: string;
  userId: number;
  nextUsageResetDate: string;
}

/**
 * Scan for active Stripe subscribers with null nextUsageResetDate
 */
export const diagnoseStripe = async (request: StripeDiagnosisRequest): Promise<StripeDiagnosisResponse> => {
  const response = await api.post('/user/bulk/diagnose-stripe', request, {
    timeout: 300000, // 5 minutes for diagnostic operations
  });
  return response.data;
};

/**
 * Fix all users with null reset dates
 */
export const autoFixStripeIssues = async (): Promise<StripeDiagnosisResponse> => {
  return await diagnoseStripe({ fix: true, limit: 1000 });
};

/**
 * Test or fix a single user's Stripe subscription issues
 */
export const diagnoseSingleUser = async (
  userId: number,
  request: SingleUserDiagnosisRequest
): Promise<StripeDiagnosisResponse> => {
  const response = await api.post(`/user/single/${userId}/diagnose-stripe`, request, {
    timeout: 60000, // 1 minute for single user operations
  });
  return response.data;
};

/**
 * Update a user's nextUsageResetDate manually
 */
export const updateUserResetDate = async (request: UpdateResetDateRequest): Promise<UpdateResetDateResponse> => {
  const response = await api.put(`/user/${request.userId}/nextUsageResetDate`, {
    nextUsageResetDate: request.nextUsageResetDate,
  });
  return response.data;
};

/**
 * Search users with complete subscription details
 */
export interface UserSearchWithSubscriptionRequest {
  query: string;
  limit?: number;
}

export interface UserWithSubscriptionDetails {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  subscriber: boolean;
  customerId: string;
  nextUsageResetDate: string | null;
  subscriptionStartDate: string | null;
  paymentType: number;
  hasActiveSubscription: boolean;
  subscriptionIssues: {
    hasIssues: boolean;
    issueType: string | null;
  };
}

export interface UserSearchWithSubscriptionResponse {
  success: boolean;
  users: UserWithSubscriptionDetails[];
  totalFound: number;
}

export const searchUsersWithSubscriptionDetails = async (
  request: UserSearchWithSubscriptionRequest
): Promise<UserSearchWithSubscriptionResponse> => {
  const response = await api.get('/user/search-with-subscription-details', {
    params: {
      query: request.query,
      limit: request.limit || 50,
    },
  });
  return response.data;
};
